/**
 * GameScene3D — Three.js scene mounted as a Mapbox custom layer.
 *
 * Geo-anchored 3D scene for active game rounds. Shares the Mapbox GL context
 * so it composites cleanly with terrain, 3D buildings, and other map layers.
 *
 * Public API:
 *   mountGameScene(map, round)   — add the custom layer, fly camera to incident
 *   dismountGameScene(map)       — remove the layer
 *   updateGameScene(map, round)  — swap in a new round without remount
 *   flyToGameScene(map, round)   — angled camera approach
 *
 * v2 scene library — category-specific low-poly scenes:
 *   police_activity → patrol car + flashing red/blue light bar
 *   pursuit         → suspect car + chasing patrol car (orbiting path)
 *   shooting        → tactical figure + muzzle flash strobe
 *   fire_ems        → fire truck + animated flame plume
 *   traffic         → tipped vehicle + traffic cones
 *   helicopter      → heli body + rotating rotor (jackpot-tier)
 *   community       → soft glow + crowd of small figures
 *   default         → pulsing alert pillar (v1 fallback)
 *
 * Each scene builder returns { group: THREE.Group; update(t: number): void }.
 * 4 choice spheres always orbit the scene root (one per multiple-choice answer)
 * so the visual is recognizably "the game" regardless of incident type.
 */

import * as THREE from 'three';
import mapboxgl from 'mapbox-gl';
import type { GameRound } from '../types';

const LAYER_ID = 'tpd-game-scene-3d';

// Category tint palette — emissive colors that match the brand
const CATEGORY_COLORS: Record<string, number> = {
  shooting:        0xff3838,
  pursuit:         0xff8c2a,
  fire_ems:        0xff5a1f,
  police_activity: 0x00f5ff,
  traffic:         0xffd700,
  containment:     0xff00aa,
  suspicious:      0xb0a8ff,
  community:       0x66ff99,
  helicopter:      0xff3838,
  other:           0xcccccc,
};

interface SceneBuilder {
  group:  THREE.Group;
  update: (elapsed: number) => void;
}

interface SceneState {
  root:          THREE.Scene;
  camera:        THREE.Camera;
  renderer:      THREE.WebGLRenderer;
  builder:       SceneBuilder;
  choiceSpheres: THREE.Mesh[];
  startedAt:     number;
  modelMatrix:   THREE.Matrix4;
  round:         GameRound;
}

let state: SceneState | null = null;

// ────────────────────────────────────────────────────────────────────────────
// Category detection — maps incident text -> scene type
// ────────────────────────────────────────────────────────────────────────────

type SceneType =
  | 'police_activity' | 'pursuit' | 'shooting' | 'fire_ems'
  | 'traffic' | 'helicopter' | 'community' | 'default';

function detectSceneType(round: GameRound): SceneType {
  const t = (round.incidentType + ' ' + round.incidentDescription).toLowerCase();
  if (/helicopter|heli|pigair|air support/.test(t)) return 'helicopter';
  if (/pursuit|chase|fleeing/.test(t))               return 'pursuit';
  if (/shoot|gun|firearm|shots fired/.test(t))       return 'shooting';
  if (/fire|smoke|hazmat|explosion|burn/.test(t))    return 'fire_ems';
  if (/traffic|collision|mva|accident|crash/.test(t))return 'traffic';
  if (/community|event|gather/.test(t))              return 'community';
  if (/officer|arrest|warrant|police|trespass/.test(t)) return 'police_activity';
  return 'default';
}

function tintForRound(round: GameRound): number {
  const type = detectSceneType(round);
  return CATEGORY_COLORS[type] ?? CATEGORY_COLORS.other;
}

// ────────────────────────────────────────────────────────────────────────────
// Reusable scene enhancers — ground, fog, particles, bloom
// ────────────────────────────────────────────────────────────────────────────

function makeGroundPlane(tint: number, radius = 60): THREE.Mesh {
  const mat = new THREE.MeshStandardMaterial({
    color: 0x080810, metalness: 0.2, roughness: 0.8,
    transparent: true, opacity: 0.85,
  });
  const geo = new THREE.CircleGeometry(radius, 32);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = -0.1;
  // Subtle tint ring at the edge
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(radius - 2, radius, 48),
    new THREE.MeshBasicMaterial({ color: tint, transparent: true, opacity: 0.12, side: THREE.DoubleSide }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0;
  mesh.add(ring);
  return mesh;
}

function makeBloomSphere(position: THREE.Vector3, color: number, radius = 8, intensity = 0.25): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.SphereGeometry(radius, 16, 12),
    new THREE.MeshBasicMaterial({
      color, transparent: true, opacity: intensity,
      depthWrite: false, blending: THREE.AdditiveBlending,
    }),
  );
  mesh.position.copy(position);
  return mesh;
}

interface Particle {
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
}

function makeParticleSystem(
  count: number,
  origin: THREE.Vector3,
  color: number,
  opts: { speed?: number; spread?: number; size?: number; lifetime?: number } = {}
): { group: THREE.Group; particles: Particle[]; update: (dt: number) => void } {
  const group = new THREE.Group();
  const particles: Particle[] = [];
  const { speed = 0.3, spread = 8, size = 1.2, lifetime = 3 } = opts;

  const mat = new THREE.MeshBasicMaterial({
    color, transparent: true, opacity: 0.6,
    depthWrite: false, blending: THREE.AdditiveBlending,
  });

  for (let i = 0; i < count; i++) {
    const mesh = new THREE.Mesh(new THREE.SphereGeometry(size * (0.5 + Math.random()), 6, 4), mat.clone());
    mesh.position.copy(origin).add(new THREE.Vector3(
      (Math.random() - 0.5) * spread,
      Math.random() * 2,
      (Math.random() - 0.5) * spread,
    ));
    group.add(mesh);
    particles.push({
      mesh,
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * speed * 0.3,
        speed * (0.5 + Math.random() * 0.5),
        (Math.random() - 0.5) * speed * 0.3,
      ),
      life: Math.random() * lifetime, // stagger start
      maxLife: lifetime * (0.7 + Math.random() * 0.6),
    });
  }

  const update = (dt: number) => {
    for (const p of particles) {
      p.life += dt;
      if (p.life > p.maxLife) {
        // Reset
        p.mesh.position.copy(origin).add(new THREE.Vector3(
          (Math.random() - 0.5) * spread, 0, (Math.random() - 0.5) * spread,
        ));
        p.life = 0;
        p.maxLife = lifetime * (0.7 + Math.random() * 0.6);
      }
      p.mesh.position.add(p.velocity.clone().multiplyScalar(dt));
      const t = p.life / p.maxLife;
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - t * t); // fade out
      p.mesh.scale.setScalar(1 + t * 0.5); // grow slightly
    }
  };

  return { group, particles, update };
}

// ────────────────────────────────────────────────────────────────────────────
// Reusable primitives
// ────────────────────────────────────────────────────────────────────────────

function makeCarBody(color: number, length = 18, width = 7, height = 5): THREE.Group {
  const g = new THREE.Group();

  // Chassis
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(length, height, width),
    new THREE.MeshStandardMaterial({ color, metalness: 0.6, roughness: 0.4 }),
  );
  body.position.y = height / 2 + 1.5;
  g.add(body);

  // Cabin (smaller box on top)
  const cabin = new THREE.Mesh(
    new THREE.BoxGeometry(length * 0.55, height * 0.6, width * 0.9),
    new THREE.MeshStandardMaterial({ color: 0x1a2030, metalness: 0.3, roughness: 0.2, transparent: true, opacity: 0.85 }),
  );
  cabin.position.set(-length * 0.1, height + 0.5, 0);
  g.add(cabin);

  // 4 wheels
  const wheelGeo = new THREE.CylinderGeometry(1.8, 1.8, 1.2, 16);
  const wheelMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.9 });
  const wheelOffsets: [number, number][] = [
    [ length * 0.35,  width / 2],
    [ length * 0.35, -width / 2],
    [-length * 0.35,  width / 2],
    [-length * 0.35, -width / 2],
  ];
  for (const [x, z] of wheelOffsets) {
    const w = new THREE.Mesh(wheelGeo, wheelMat);
    w.position.set(x, 1.8, z);
    w.rotation.x = Math.PI / 2;
    g.add(w);
  }
  return g;
}

function makeFigure(color: number, height = 10): THREE.Group {
  const g = new THREE.Group();
  const torso = new THREE.Mesh(
    new THREE.BoxGeometry(3.5, height * 0.55, 2),
    new THREE.MeshStandardMaterial({ color, roughness: 0.6 }),
  );
  torso.position.y = height * 0.35;
  g.add(torso);
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(1.7, 16, 12),
    new THREE.MeshStandardMaterial({ color: 0xeec39a, roughness: 0.7 }),
  );
  head.position.y = height * 0.75;
  g.add(head);
  return g;
}

function makeChoiceSpheres(tint: number): THREE.Mesh[] {
  const spheres: THREE.Mesh[] = [];
  for (let i = 0; i < 4; i++) {
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(4, 20, 16),
      new THREE.MeshStandardMaterial({
        color:             0x0a1320,
        emissive:          tint,
        emissiveIntensity: 0.65,
        metalness:         0.7,
        roughness:         0.3,
      }),
    );
    spheres.push(sphere);
  }
  return spheres;
}

// ────────────────────────────────────────────────────────────────────────────
// Scene builders — one per category
// ────────────────────────────────────────────────────────────────────────────

function buildPoliceActivity(tint: number): SceneBuilder {
  const group = new THREE.Group();
  const car = makeCarBody(0x0e1a2f); // dark blue patrol car
  group.add(car);

  // Light bar: two side-by-side bars that strobe red/blue
  const barGeo = new THREE.BoxGeometry(7, 1.4, 2);
  const redMat  = new THREE.MeshStandardMaterial({ color: 0xff3030, emissive: 0xff0000, emissiveIntensity: 1.5 });
  const blueMat = new THREE.MeshStandardMaterial({ color: 0x3060ff, emissive: 0x0040ff, emissiveIntensity: 1.5 });
  const redBar  = new THREE.Mesh(barGeo, redMat);
  const blueBar = new THREE.Mesh(barGeo, blueMat);
  redBar.position.set(-1.5, 10, 0);
  blueBar.position.set( 1.5, 10, 0);
  car.add(redBar);
  car.add(blueBar);

  // Officer figure beside the car
  const officer = makeFigure(0x0a1525, 9);
  officer.position.set(-14, 0, -4);
  group.add(officer);

  // Bloom glow on light bar
  const redBloom = makeBloomSphere(new THREE.Vector3(-1.5, 10, 0), 0xff0000, 5, 0.3);
  const blueBloom = makeBloomSphere(new THREE.Vector3(1.5, 10, 0), 0x0040ff, 5, 0.3);
  car.add(redBloom);
  car.add(blueBloom);

  // Headlights
  const headlight = new THREE.SpotLight(0xfff4cc, 1.2, 80, Math.PI / 5, 0.4);
  headlight.position.set(-9, 5, 0);
  headlight.target.position.set(-30, 0, 0);
  car.add(headlight);
  car.add(headlight.target);

  // Soft red/blue ground light
  const light = new THREE.PointLight(tint, 1.5, 200, 1.5);
  light.position.set(0, 30, 0);
  group.add(light);

  return {
    group,
    update: (t) => {
      // Strobe: 8Hz, alternating
      const pulse = (Math.sin(t * 16) + 1) / 2;
      redMat.emissiveIntensity  = 0.4 + pulse * 1.8;
      blueMat.emissiveIntensity = 0.4 + (1 - pulse) * 1.8;
      officer.rotation.y = Math.sin(t * 0.4) * 0.25; // gentle look-around
    },
  };
}

function buildPursuit(tint: number): SceneBuilder {
  const group = new THREE.Group();
  const suspect = makeCarBody(0x0d0d0d); // black suspect car
  const patrol  = makeCarBody(0x0e1a2f); // patrol car

  // Patrol gets a strobing light bar too
  const barGeo  = new THREE.BoxGeometry(6, 1.2, 2);
  const redMat  = new THREE.MeshStandardMaterial({ color: 0xff3030, emissive: 0xff0000, emissiveIntensity: 1.5 });
  const blueMat = new THREE.MeshStandardMaterial({ color: 0x3060ff, emissive: 0x0040ff, emissiveIntensity: 1.5 });
  const redBar  = new THREE.Mesh(barGeo, redMat);
  const blueBar = new THREE.Mesh(barGeo, blueMat);
  redBar.position.set(-1.3, 10, 0);
  blueBar.position.set( 1.3, 10, 0);
  patrol.add(redBar);
  patrol.add(blueBar);

  // Make suspect car visible — dark red instead of black
  const suspectBody = suspect.children[0] as THREE.Mesh;
  if (suspectBody?.material) (suspectBody.material as THREE.MeshStandardMaterial).color.setHex(0x8b1a1a);

  // Light bar bloom on patrol
  patrol.add(makeBloomSphere(new THREE.Vector3(-1.3, 10, 0), 0xff0000, 4, 0.35));
  patrol.add(makeBloomSphere(new THREE.Vector3(1.3, 10, 0), 0x0040ff, 4, 0.35));

  group.add(suspect, patrol);

  const light = new THREE.PointLight(tint, 1.4, 220, 1.2);
  light.position.set(0, 35, 0);
  group.add(light);

  // Tire smoke particles behind each car
  const suspectSmoke = makeParticleSystem(8, new THREE.Vector3(0, 1, 0), 0xccccaa, { speed: 0.15, spread: 3, size: 0.8, lifetime: 1.5 });
  const patrolSmoke = makeParticleSystem(8, new THREE.Vector3(0, 1, 0), 0xccccaa, { speed: 0.15, spread: 3, size: 0.8, lifetime: 1.5 });
  group.add(suspectSmoke.group);
  group.add(patrolSmoke.group);

  return {
    group,
    update: (t) => {
      const angle  = t * 0.7;
      const radius = 28 + Math.sin(t * 3) * 3; // swerving orbit
      suspect.position.set(Math.cos(angle) * radius, 0, Math.sin(angle) * radius);
      suspect.rotation.y = -angle + Math.PI / 2;

      const lag   = 0.5;
      patrol.position.set(Math.cos(angle - lag) * radius, 0, Math.sin(angle - lag) * radius);
      patrol.rotation.y = -(angle - lag) + Math.PI / 2;

      // Move smoke emitters to car positions
      suspectSmoke.group.position.copy(suspect.position);
      patrolSmoke.group.position.copy(patrol.position);
      suspectSmoke.update(0.016);
      patrolSmoke.update(0.016);

      const strobe = (Math.sin(t * 16) + 1) / 2;
      redMat.emissiveIntensity  = 0.4 + strobe * 1.8;
      blueMat.emissiveIntensity = 0.4 + (1 - strobe) * 1.8;
    },
  };
}

function buildShooting(tint: number): SceneBuilder {
  const group = new THREE.Group();
  const figure = makeFigure(0x111111, 12);
  figure.position.set(0, 0, 0);
  group.add(figure);

  // Muzzle flash — small sphere + intense point light
  const flash = new THREE.Mesh(
    new THREE.SphereGeometry(1.8, 16, 12),
    new THREE.MeshBasicMaterial({ color: 0xffd96b, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending }),
  );
  flash.position.set(5, 9, 0);
  group.add(flash);

  const flashLight = new THREE.PointLight(0xffd96b, 0, 180, 1.5);
  flashLight.position.set(5, 9, 0);
  group.add(flashLight);

  // Police perimeter — 3 cones
  const coneGeo = new THREE.ConeGeometry(2.2, 5, 16);
  const coneMat = new THREE.MeshStandardMaterial({ color: 0xff6a00, emissive: 0xff3300, emissiveIntensity: 0.4 });
  for (let i = 0; i < 3; i++) {
    const angle = (i / 3) * Math.PI * 2 + 0.4;
    const cone = new THREE.Mesh(coneGeo, coneMat);
    cone.position.set(Math.cos(angle) * 22, 2.5, Math.sin(angle) * 22);
    group.add(cone);
  }

  return {
    group,
    update: (t) => {
      // Strobe at uneven cadence — 3 quick shots, pause
      const phase = (t * 2) % 4;
      const fire  = phase < 0.18 || (phase > 0.4 && phase < 0.58);
      flashLight.intensity = fire ? 3.5 : 0;
      (flash.material as THREE.MeshBasicMaterial).opacity = fire ? 1 : 0.05;
      flash.scale.setScalar(fire ? 1.4 : 0.5);
      figure.rotation.y = Math.sin(t * 0.6) * 0.15;
    },
  };
}

function buildFireEms(tint: number): SceneBuilder {
  const group = new THREE.Group();

  // Fire truck — red, slightly longer than a patrol car
  const truck = makeCarBody(0xc31010, 24, 8, 7);
  group.add(truck);

  // Flame plume — stacked spheres with offset, animated emissive
  const flameMats: THREE.MeshBasicMaterial[] = [];
  const flameLayers: THREE.Mesh[] = [];
  for (let i = 0; i < 4; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color:       i === 0 ? 0xffd96b : 0xff5a1f,
      transparent: true,
      opacity:     0.7 - i * 0.15,
      blending:    THREE.AdditiveBlending,
      depthWrite:  false,
    });
    const mesh = new THREE.Mesh(new THREE.SphereGeometry(5 + i * 2, 16, 12), mat);
    mesh.position.set(15, 8 + i * 3, 5);
    group.add(mesh);
    flameMats.push(mat);
    flameLayers.push(mesh);
  }

  // Building with glowing window cutouts (internal fire)
  const building = new THREE.Mesh(
    new THREE.BoxGeometry(18, 18, 10),
    new THREE.MeshStandardMaterial({ color: 0x1a0d05, roughness: 0.9 }),
  );
  building.position.set(22, 9, 5);
  group.add(building);

  // Window glow — orange emissive rectangles on the building face
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 2; col++) {
      const win = new THREE.Mesh(
        new THREE.PlaneGeometry(3, 3.5),
        new THREE.MeshBasicMaterial({ color: 0xff6020, transparent: true, opacity: 0.7 + Math.random() * 0.3, blending: THREE.AdditiveBlending }),
      );
      win.position.set(22 - 9.1, 4 + row * 5, col * 5 + 1);
      group.add(win);
    }
  }

  // Smoke column rising above flames
  const smoke = makeParticleSystem(20, new THREE.Vector3(18, 20, 5), 0x333333, {
    speed: 0.4, spread: 6, size: 2.5, lifetime: 4,
  });
  group.add(smoke.group);

  // Ember/spark particles
  const embers = makeParticleSystem(12, new THREE.Vector3(15, 10, 5), 0xff8800, {
    speed: 0.6, spread: 10, size: 0.4, lifetime: 2,
  });
  group.add(embers.group);

  // 2 firefighter figures
  const ff1 = makeFigure(0xc31010, 9);
  ff1.position.set(5, 0, 8);
  group.add(ff1);
  const ff2 = makeFigure(0xc31010, 9);
  ff2.position.set(3, 0, -3);
  group.add(ff2);

  // Warm orange ground light
  const light = new THREE.PointLight(0xff7020, 2.0, 200, 1.5);
  light.position.set(15, 25, 5);
  group.add(light);

  // Bloom around the fire
  group.add(makeBloomSphere(new THREE.Vector3(15, 12, 5), 0xff5a1f, 15, 0.2));

  return {
    group,
    update: (t) => {
      // Flicker the flame layers individually
      flameLayers.forEach((m, i) => {
        const w = 0.85 + 0.3 * Math.sin(t * (4 + i * 0.6) + i);
        m.scale.setScalar(w);
        m.position.y = (8 + i * 3) + Math.sin(t * 3 + i * 0.7) * 0.8;
        flameMats[i].opacity = (0.7 - i * 0.15) * (0.7 + 0.3 * Math.sin(t * 5 + i));
      });
      light.intensity = 1.6 + Math.sin(t * 7) * 0.5;
      smoke.update(0.016);
      embers.update(0.016);
      ff1.rotation.y = Math.sin(t * 0.5) * 0.3;
      ff2.rotation.y = Math.sin(t * 0.5 + 1) * 0.3;
    },
  };
}

function buildTraffic(tint: number): SceneBuilder {
  const group = new THREE.Group();

  // Tipped vehicle (rotated 35deg)
  const vehicle = makeCarBody(0x556070);
  vehicle.rotation.z = THREE.MathUtils.degToRad(35);
  vehicle.position.y = 2;
  group.add(vehicle);

  // Other vehicle that hit it
  const other = makeCarBody(0x202830);
  other.position.set(-22, 0, -4);
  other.rotation.y = 0.25;
  group.add(other);

  // Traffic cones in a ring
  const coneGeo = new THREE.ConeGeometry(1.8, 4.5, 16);
  const coneMat = new THREE.MeshStandardMaterial({ color: 0xff8c2a, emissive: 0xff5a1f, emissiveIntensity: 0.5 });
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2;
    const cone = new THREE.Mesh(coneGeo, coneMat);
    cone.position.set(Math.cos(angle) * 25, 2.2, Math.sin(angle) * 25);
    group.add(cone);
  }

  // Soft yellow ground light
  const light = new THREE.PointLight(0xffd700, 1.2, 200, 1.2);
  light.position.set(0, 30, 0);
  group.add(light);

  return {
    group,
    update: (t) => {
      // Smoke wisp — handled by light flicker
      light.intensity = 1.0 + 0.25 * Math.sin(t * 1.5);
    },
  };
}

function buildHelicopter(tint: number): SceneBuilder {
  const group = new THREE.Group();

  // Body (capsule via ellipsoid)
  const body = new THREE.Mesh(
    new THREE.SphereGeometry(5, 24, 12),
    new THREE.MeshStandardMaterial({ color: 0x1a2a3a, metalness: 0.7, roughness: 0.3 }),
  );
  body.scale.set(2.2, 1, 1);
  body.position.y = 35;
  group.add(body);

  // Tail
  const tail = new THREE.Mesh(
    new THREE.BoxGeometry(14, 1.5, 1.5),
    new THREE.MeshStandardMaterial({ color: 0x1a2a3a, metalness: 0.7, roughness: 0.4 }),
  );
  tail.position.set(11, 35, 0);
  group.add(tail);

  // Landing skids
  const skidMat = new THREE.MeshStandardMaterial({ color: 0x222222, metalness: 0.5, roughness: 0.6 });
  const skid1 = new THREE.Mesh(new THREE.BoxGeometry(14, 0.5, 0.5), skidMat);
  skid1.position.set(0, 29, 3);
  group.add(skid1);
  const skid2 = new THREE.Mesh(new THREE.BoxGeometry(14, 0.5, 0.5), skidMat);
  skid2.position.set(0, 29, -3);
  group.add(skid2);

  // Main rotor — crossed blades
  const rotorGroup = new THREE.Group();
  const blade1 = new THREE.Mesh(
    new THREE.BoxGeometry(28, 0.4, 1.2),
    new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.9 }),
  );
  const blade2 = blade1.clone();
  blade2.rotation.y = Math.PI / 2;
  rotorGroup.add(blade1, blade2);
  rotorGroup.position.y = 40;
  group.add(rotorGroup);

  // Rotor blur disc
  const rotorDisc = new THREE.Mesh(
    new THREE.CircleGeometry(14, 32),
    new THREE.MeshBasicMaterial({ color: 0xaaaaaa, transparent: true, opacity: 0.06, side: THREE.DoubleSide, depthWrite: false }),
  );
  rotorDisc.rotation.x = Math.PI / 2;
  rotorDisc.position.y = 40;
  group.add(rotorDisc);

  // Navigation lights
  const navRed = new THREE.PointLight(0xff0000, 0.8, 30);
  navRed.position.set(-5, 35, 5); // port
  group.add(navRed);
  const navGreen = new THREE.PointLight(0x00ff00, 0.8, 30);
  navGreen.position.set(-5, 35, -5); // starboard
  group.add(navGreen);
  const navWhite = new THREE.PointLight(0xffffff, 0.5, 20);
  navWhite.position.set(17, 36, 0); // tail strobe
  group.add(navWhite);

  // Tail rotor
  const tailRotor = new THREE.Mesh(
    new THREE.BoxGeometry(4, 0.3, 0.8),
    new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.9 }),
  );
  tailRotor.position.set(17, 36, 0);
  tailRotor.rotation.x = Math.PI / 2;
  group.add(tailRotor);

  // Searchlight cone — angled toward the ground
  const beamMat = new THREE.MeshBasicMaterial({
    color: 0xfff2c4, transparent: true, opacity: 0.18,
    blending: THREE.AdditiveBlending, depthWrite: false,
  });
  const beam = new THREE.Mesh(new THREE.ConeGeometry(20, 35, 24, 1, true), beamMat);
  beam.position.y = 17;
  beam.rotation.z = Math.PI; // tip pointing down? cone tip is +Y by default, rotate so tip is at bottom
  // Actually ConeGeometry tip at +Y/2; rotate 180 around X so tip is at -Y
  beam.rotation.x = Math.PI;
  group.add(beam);

  const beamLight = new THREE.SpotLight(0xfff2c4, 2.5, 200, Math.PI / 6, 0.4, 1.5);
  beamLight.position.set(0, 35, 0);
  beamLight.target.position.set(0, 0, 0);
  group.add(beamLight, beamLight.target);

  return {
    group,
    update: (t) => {
      // Rotor spins fast; tail rotor faster
      rotorGroup.rotation.y = t * 18;
      rotorDisc.rotation.y = t * 5; // slow spin for visual
      tailRotor.rotation.y = t * 28;
      // Helicopter circles slowly + bobs
      group.rotation.y = t * 0.25;
      const bobY = 35 + Math.sin(t * 0.8) * 1.5;
      body.position.y = bobY;
      tail.position.y = bobY;
      rotorGroup.position.y = bobY + 5;
      rotorDisc.position.y = bobY + 5;
      skid1.position.y = bobY - 6;
      skid2.position.y = bobY - 6;

      // Searchlight sweep — target orbits on the ground
      const sweepAngle = t * 0.4;
      beamLight.target.position.set(Math.cos(sweepAngle) * 15, 0, Math.sin(sweepAngle) * 15);
      beam.position.set(Math.cos(sweepAngle) * 5, 17, Math.sin(sweepAngle) * 5);

      // Tail strobe — 1Hz flash
      navWhite.intensity = Math.sin(t * 6) > 0.8 ? 3 : 0;
    },
  };
}

function buildCommunity(tint: number): SceneBuilder {
  const group = new THREE.Group();

  // Small crowd: 6 figures in random spots
  const figures: THREE.Group[] = [];
  for (let i = 0; i < 6; i++) {
    const fig = makeFigure(
      [0x3a6cd9, 0x66ff99, 0xffd700, 0xff8c2a, 0x9c6cff, 0xff6699][i % 6],
      8,
    );
    const angle = (i / 6) * Math.PI * 2;
    fig.position.set(Math.cos(angle) * 12, 0, Math.sin(angle) * 12);
    fig.rotation.y = -angle + Math.PI;
    group.add(fig);
    figures.push(fig);
  }

  // Soft green community glow
  const light = new THREE.PointLight(0x66ff99, 1.3, 220, 1.3);
  light.position.set(0, 25, 0);
  group.add(light);

  return {
    group,
    update: (t) => {
      figures.forEach((f, i) => {
        f.position.y = Math.abs(Math.sin(t * 2 + i * 0.6)) * 1.5; // gentle bob
      });
      light.intensity = 1.0 + 0.3 * Math.sin(t * 1.2);
    },
  };
}

function buildDefault(tint: number): SceneBuilder {
  const group = new THREE.Group();
  const pillar = new THREE.Mesh(
    new THREE.CylinderGeometry(6, 12, 60, 24, 1, false),
    new THREE.MeshStandardMaterial({
      color:             tint,
      emissive:          tint,
      emissiveIntensity: 0.9,
      metalness:         0.5,
      roughness:         0.25,
      transparent:       true,
      opacity:           0.92,
    }),
  );
  pillar.position.y = 30;
  group.add(pillar);

  const glow = new THREE.Mesh(
    new THREE.SphereGeometry(28, 24, 16),
    new THREE.MeshBasicMaterial({
      color: tint, transparent: true, opacity: 0.18,
      depthWrite: false, blending: THREE.AdditiveBlending,
    }),
  );
  glow.position.y = 30;
  group.add(glow);

  const light = new THREE.PointLight(tint, 1.5, 250, 1.2);
  light.position.set(0, 60, 0);
  group.add(light);

  return {
    group,
    update: (t) => {
      const pulse = 0.85 + 0.15 * Math.sin(t * 3.2);
      pillar.scale.set(pulse, 1, pulse);
      (pillar.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.6 + 0.5 * Math.sin(t * 4);
      glow.scale.setScalar(1 + 0.25 * Math.sin(t * 2));
      (glow.material as THREE.MeshBasicMaterial).opacity = 0.12 + 0.10 * (0.5 + 0.5 * Math.sin(t * 1.8));
    },
  };
}

const SCENE_BUILDERS: Record<SceneType, (tint: number) => SceneBuilder> = {
  police_activity: buildPoliceActivity,
  pursuit:         buildPursuit,
  shooting:        buildShooting,
  fire_ems:        buildFireEms,
  traffic:         buildTraffic,
  helicopter:      buildHelicopter,
  community:       buildCommunity,
  default:         buildDefault,
};

// ────────────────────────────────────────────────────────────────────────────
// Mapbox custom layer plumbing
// ────────────────────────────────────────────────────────────────────────────

function buildSceneForRound(round: GameRound): {
  scene:         THREE.Scene;
  builder:       SceneBuilder;
  choiceSpheres: THREE.Mesh[];
} {
  const scene = new THREE.Scene();
  const tint  = tintForRound(round);
  const type  = detectSceneType(round);

  // Ambient + hemisphere fill so meshes aren't black on the dark basemap
  scene.add(new THREE.AmbientLight(0xffffff, 0.35));
  scene.add(new THREE.HemisphereLight(0x88ccff, 0x331100, 0.45));

  // Ground plane — grounds every scene
  scene.add(makeGroundPlane(tint));

  // Fog — adds atmospheric depth
  scene.fog = new THREE.FogExp2(0x0a0a1a, 0.004);

  const builder = SCENE_BUILDERS[type](tint);
  scene.add(builder.group);

  // Bloom glow at scene center
  scene.add(makeBloomSphere(new THREE.Vector3(0, 15, 0), tint, 20, 0.12));

  // Atmospheric dust particles
  const dust = makeParticleSystem(15, new THREE.Vector3(0, 0, 0), tint, {
    speed: 0.1, spread: 40, size: 0.6, lifetime: 5
  });
  scene.add(dust.group);

  // Wrap the builder's update to include particle updates
  const origUpdate = builder.update;
  builder.update = (t: number) => {
    origUpdate(t);
    dust.update(0.016); // ~60fps dt
  };

  // 4 choice spheres always orbit
  const choiceSpheres = makeChoiceSpheres(tint);
  for (const s of choiceSpheres) scene.add(s);

  return { scene, builder, choiceSpheres };
}

function makeModelTransform(round: GameRound): THREE.Matrix4 {
  const merc = mapboxgl.MercatorCoordinate.fromLngLat(
    [round.incidentLocation.lng, round.incidentLocation.lat], 0,
  );
  const meterScale = merc.meterInMercatorCoordinateUnits();
  return new THREE.Matrix4()
    .makeTranslation(merc.x, merc.y, merc.z || 0)
    .scale(new THREE.Vector3(meterScale, -meterScale, meterScale))
    .multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2));
}

export function mountGameScene(map: mapboxgl.Map, round: GameRound) {
  if (!map.getStyle()) return;
  dismountGameScene(map);

  const { scene, builder, choiceSpheres } = buildSceneForRound(round);
  const camera = new THREE.Camera();
  const renderer = new THREE.WebGLRenderer({
    canvas:    map.getCanvas(),
    context:   map.painter.context.gl,
    antialias: true,
  });
  renderer.autoClear = false;

  state = {
    root:         scene,
    camera,
    renderer,
    builder,
    choiceSpheres,
    startedAt:    performance.now(),
    modelMatrix:  makeModelTransform(round),
    round,
  };

  const layer: mapboxgl.CustomLayerInterface = {
    id:             LAYER_ID,
    type:           'custom',
    renderingMode:  '3d',
    onAdd: () => {},
    render: (_gl, matrix) => {
      if (!state) return;
      const elapsed = (performance.now() - state.startedAt) / 1000;

      // Per-category animation tick
      state.builder.update(elapsed);

      // Orbit choice spheres around the scene
      state.choiceSpheres.forEach((s, i) => {
        const angle = (i / 4) * Math.PI * 2 + elapsed * 0.6;
        s.position.x = Math.cos(angle) * 42;
        s.position.z = Math.sin(angle) * 42;
        s.position.y = 22 + Math.sin(elapsed * 2 + i) * 4;
        s.rotation.y = elapsed * 1.5;
      });

      const m = new THREE.Matrix4().fromArray(matrix);
      state.camera.projectionMatrix = m.multiply(state.modelMatrix);
      state.renderer.resetState();
      state.renderer.render(state.root, state.camera);
      map.triggerRepaint();
    },
  };

  map.addLayer(layer);
}

export function dismountGameScene(map: mapboxgl.Map) {
  if (map.getLayer(LAYER_ID)) {
    try { map.removeLayer(LAYER_ID); } catch { /* nbd */ }
  }
  if (state) {
    state.root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      mesh.geometry?.dispose?.();
      const m = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(m)) m.forEach(mat => mat.dispose());
      else m?.dispose?.();
    });
    state = null;
  }
}

export function updateGameScene(map: mapboxgl.Map, round: GameRound) {
  if (!state) { mountGameScene(map, round); return; }
  if (state.round.id === round.id) return;
  mountGameScene(map, round);
}

export function flyToGameScene(map: mapboxgl.Map, round: GameRound) {
  map.flyTo({
    center:  [round.incidentLocation.lng, round.incidentLocation.lat],
    zoom:    17.2,
    pitch:   62,
    bearing: Math.random() * 360,
    speed:   1.1,
    curve:   1.6,
    essential: true,
  });
}
